import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, ROLE_PUBLICADOR, ROLE_SI_ADMIN } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { publicacaoService } from './publicacaoService';
import { CANAL_SI } from './publicacao.definitions';
import type { PessoaLogada } from './publicacaoService';
import type { AutorPublicacao, MeuEstadoPublicacao, Publicacao, PublicacaoEditavel } from './publicacao.types';

export const PUBLICACOES_QUERY_KEYS = {
  all: ['si', 'publicacoes'] as const,
  publicadas: () => [...PUBLICACOES_QUERY_KEYS.all, 'publicadas'] as const,
  gestao: () => [...PUBLICACOES_QUERY_KEYS.all, 'gestao'] as const,
  detalhe: (id: string) => [...PUBLICACOES_QUERY_KEYS.all, 'detalhe', id] as const,
  estados: (email: string, ids: string[]) => [...PUBLICACOES_QUERY_KEYS.all, 'estados', email, ...ids] as const,
  comentarios: (id: string) => [...PUBLICACOES_QUERY_KEYS.all, 'comentarios', id] as const,
  registros: (id: string, sub: string) => [...PUBLICACOES_QUERY_KEYS.all, 'registros', id, sub] as const,
  versoes: (id: string) => [...PUBLICACOES_QUERY_KEYS.all, 'versoes', id] as const,
};

/** Posts: só "Publicar no Portal". */
export function usePodePublicar(): boolean {
  const { userHasRole } = useAuth();
  return userHasRole(ROLE_PUBLICADOR);
}

/** Documentos: "Publicar no Portal" ou Administrar SI (material de referência do SI). */
export function usePodeGerirDocumentos(): boolean {
  const { userHasRole } = useAuth();
  return userHasRole(ROLE_PUBLICADOR) || userHasRole(ROLE_SI_ADMIN);
}

export function useVersoesDocumentoQuery(id: string | undefined) {
  const podeGerir = usePodeGerirDocumentos();
  return useQuery({
    queryKey: PUBLICACOES_QUERY_KEYS.versoes(id ?? ''),
    queryFn: () => publicacaoService.listarVersoes(id as string),
    enabled: Boolean(id) && podeGerir,
  });
}

/** Quem está logado, como autor (comentários, auditoria) e como pessoa (leitura/ciência). */
export function usePessoaLogada(): { autor: AutorPublicacao | null; pessoa: PessoaLogada | null } {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user?.email) return { autor: null, pessoa: null };
    const nome = user.displayName ?? user.email;
    return {
      autor: { id: user.collaboratorId ?? user.uid, name: nome, avatar: user.photoURL ?? null },
      pessoa: { email: user.email, colaboradorId: user.collaboratorId ?? null, nome, avatar: user.photoURL ?? null },
    };
  }, [user]);
}

export function usePublicacoesPublicadasQuery() {
  return useQuery({ queryKey: PUBLICACOES_QUERY_KEYS.publicadas(), queryFn: () => publicacaoService.listarPublicadas() });
}

/** Aba Publicações: quem publica e o Administrar SI veem tudo (o SI vê os posts em modo leitura). */
export function usePublicacoesGestaoQuery() {
  const podeGerirDocumentos = usePodeGerirDocumentos();
  const podePublicar = usePodePublicar();
  return useQuery({
    queryKey: [...PUBLICACOES_QUERY_KEYS.gestao(), podePublicar ? 'publicador' : 'si'],
    queryFn: () => publicacaoService.listarTodas(podePublicar),
    enabled: podeGerirDocumentos,
  });
}

export function usePublicacaoQuery(id: string | undefined) {
  return useQuery({
    queryKey: PUBLICACOES_QUERY_KEYS.detalhe(id ?? ''),
    // Só publicações do canal SI aparecem no Portal do SI (link de outro portal = "não encontrada").
    queryFn: async () => {
      const publicacao = await publicacaoService.obter(id as string).catch(() => null);
      return publicacao && (publicacao.canal ?? CANAL_SI) === CANAL_SI ? publicacao : null;
    },
    enabled: Boolean(id),
  });
}

/** Lida / ciência / curtida da pessoa em cada publicação (3 leituras por publicação). */
export function useMeusEstadosQuery(publicacoes: Publicacao[]) {
  const { pessoa } = usePessoaLogada();
  const ids = useMemo(() => publicacoes.map((p) => p.id).sort(), [publicacoes]);
  const consulta = useQuery({
    queryKey: PUBLICACOES_QUERY_KEYS.estados(pessoa?.email ?? '', ids),
    queryFn: async () => {
      const estados = await Promise.all(ids.map((id) => publicacaoService.meuEstado(id, pessoa!.email)));
      return new Map(ids.map((id, i) => [id, estados[i]]));
    },
    enabled: Boolean(pessoa?.email) && ids.length > 0,
  });
  return { ...consulta, estados: consulta.data ?? new Map<string, MeuEstadoPublicacao>() };
}

export function useComentariosPublicacaoQuery(id: string | undefined) {
  return useQuery({
    queryKey: PUBLICACOES_QUERY_KEYS.comentarios(id ?? ''),
    queryFn: () => publicacaoService.listarComentarios(id as string),
    enabled: Boolean(id),
  });
}

export function useRegistrosPublicacaoQuery(id: string | undefined, sub: 'leituras' | 'ciencias') {
  const podeGerir = usePodeGerirDocumentos();
  return useQuery({
    queryKey: PUBLICACOES_QUERY_KEYS.registros(id ?? '', sub),
    queryFn: () => publicacaoService.registros(id as string, sub),
    enabled: Boolean(id) && podeGerir,
  });
}

/** Ações de quem publica (editor e lista). Erros viram toast; cada uma invalida as publicações. */
export function usePublicacaoGestaoMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { autor } = usePessoaLogada();
  const invalidar = () => queryClient.invalidateQueries({ queryKey: PUBLICACOES_QUERY_KEYS.all });
  const comErro = (acao: string) => (error: unknown) =>
    toast.error(`Erro ao ${acao}: ${(error as { message?: string })?.message ?? 'erro desconhecido'}`);
  const exigirAutor = () => {
    if (!autor) throw new Error('Faça login novamente.');
    return autor;
  };

  return {
    criar: useMutation({
      mutationFn: (dados: PublicacaoEditavel) => publicacaoService.criar(dados, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('criar a publicação'),
    }),
    salvar: useMutation({
      mutationFn: ({ id, dados }: { id: string; dados: PublicacaoEditavel }) => publicacaoService.salvar(id, dados, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('salvar'),
    }),
    publicar: useMutation({
      mutationFn: ({ id, quando }: { id: string; quando: Date | null }) => publicacaoService.publicar(id, quando, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('publicar'),
    }),
    voltarParaRascunho: useMutation({
      mutationFn: (id: string) => publicacaoService.voltarParaRascunho(id, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('voltar para rascunho'),
    }),
    despublicar: useMutation({
      mutationFn: (id: string) => publicacaoService.despublicar(id, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('despublicar'),
    }),
    pedirNovaCiencia: useMutation({
      mutationFn: (id: string) => publicacaoService.pedirNovaCiencia(id, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('pedir nova ciência'),
    }),
    fixar: useMutation({
      mutationFn: ({ id, ate }: { id: string; ate: Date | null }) => publicacaoService.fixar(id, ate, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('fixar'),
    }),
    desafixar: useMutation({
      mutationFn: (id: string) => publicacaoService.desafixar(id, exigirAutor()),
      onSuccess: invalidar,
      onError: comErro('desafixar'),
    }),
    excluir: useMutation({
      mutationFn: (id: string) => publicacaoService.excluir(id),
      onSuccess: () => {
        toast.success('Publicação excluída.');
        invalidar();
      },
      onError: comErro('excluir'),
    }),
  };
}

/** Ações de quem lê: marcar lida, ciência, curtir, comentar. */
export function usePublicacaoLeitorMutations(publicacaoId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { autor, pessoa } = usePessoaLogada();
  const { userHasRole } = useAuth();
  const autorSelo = userHasRole(ROLE_SI_ADMIN) ? 'si' : userHasRole(ROLE_PUBLICADOR) ? 'comunicacao' : null;
  const invalidarEstado = () => queryClient.invalidateQueries({ queryKey: PUBLICACOES_QUERY_KEYS.all });
  const invalidarComentarios = () =>
    queryClient.invalidateQueries({ queryKey: PUBLICACOES_QUERY_KEYS.comentarios(publicacaoId) });
  const comErro = (acao: string) => (error: unknown) =>
    toast.error(`Erro ao ${acao}: ${(error as { message?: string })?.message ?? 'erro desconhecido'}`);

  return {
    marcarLida: useMutation({
      mutationFn: () => (pessoa ? publicacaoService.marcarLida(publicacaoId, pessoa) : Promise.resolve()),
      onSuccess: invalidarEstado,
    }),
    confirmarCiencia: useMutation({
      mutationFn: (versao: number) => publicacaoService.confirmarCiencia(publicacaoId, versao, pessoa!),
      onSuccess: () => {
        toast.success('Ciência confirmada.');
        invalidarEstado();
      },
      onError: comErro('confirmar a ciência'),
    }),
    curtir: useMutation({
      mutationFn: (curtir: boolean) => publicacaoService.curtir(publicacaoId, pessoa!, curtir),
      onSuccess: invalidarEstado,
      onError: comErro('curtir'),
    }),
    comentar: useMutation({
      mutationFn: (texto: string) => publicacaoService.comentar(publicacaoId, texto, autor!, pessoa!.email, autorSelo),
      onSuccess: () => {
        invalidarComentarios();
        invalidarEstado();
      },
      onError: comErro('comentar'),
    }),
    editarComentario: useMutation({
      mutationFn: ({ comentarioId, texto }: { comentarioId: string; texto: string }) =>
        publicacaoService.editarComentario(publicacaoId, comentarioId, texto),
      onSuccess: invalidarComentarios,
      onError: comErro('editar o comentário'),
    }),
    excluirComentario: useMutation({
      mutationFn: (comentarioId: string) => publicacaoService.excluirComentario(publicacaoId, comentarioId),
      onSuccess: () => {
        invalidarComentarios();
        invalidarEstado();
      },
      onError: comErro('excluir o comentário'),
    }),
  };
}
